import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {ChevronDown} from 'lucide-react';

const FAQPage: React.FC = () => {
  const { pathname } = useLocation();
  const currentLang = pathname.startsWith('/en') ? 'en' : 'zh';
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Schema Data
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://www.ongucoffee.com/faq#webpage",
        "url": "https://www.ongucoffee.com/faq",
        "name": "FAQ｜Ongu Studio – Coffee Roasting（信義）｜門市與商用咖啡豆批發常見問題",
        "description": "整理 ONGU Studio 信義店與商用咖啡豆批發常見問題：是否算信義區咖啡廳推薦、內用外帶、現場烘豆、咖啡豆選購、手沖風味、熱門時段建議，以及批發最低訂購量、試用包、研磨、吧台與豆單規劃。",
        "isPartOf": {
          "@type": "WebSite",
          "@id": "https://www.ongucoffee.com/#website",
          "url": "https://www.ongucoffee.com/",
          "name": "Ongu Studio – Coffee Roasting"
        },
        "about": {
          "@type": "Organization",
          "@id": "https://www.ongucoffee.com/#organization",
          "name": "Ongu Studio – Coffee Roasting",
          "url": "https://www.ongucoffee.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.ongucoffee.com/logo.png"
          }
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "url": "https://www.ongucoffee.com/og/faq.jpg",
          "width": 1200,
          "height": 630
        },
        "inLanguage": "zh-TW"
      },
      {
        "@type": "FAQPage",
        "@id": "https://www.ongucoffee.com/faq#faq",
        "url": "https://www.ongucoffee.com/faq",
        "name": "FAQ｜Ongu Studio – Coffee Roasting（信義）",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "ONGU Studio 信義店算信義區咖啡廳推薦嗎？特色是什麼？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "是的，ONGU Studio 信義店主打「職人現場烘豆＋季節手沖」與金屬工業設計空間。座位不多，但能近距離看見製作過程，適合想在信義區喝到乾淨、層次清晰風味的人。"
            }
          },
          {
            "@type": "Question",
            "name": "這裡比較適合外帶還是內用？有座位嗎？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "店內座位精簡，保留寬敞的職人操作區與展示區。你可以內用短停品飲，也很適合在信義區外帶咖啡、順路帶走一杯手沖或奶咖。"
            }
          },
          {
            "@type": "Question",
            "name": "信義店有現場烘豆嗎？每天都有嗎？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "有的，ONGU 信義店以現場烘豆為核心特色之一。烘豆師會監控溫度、時間、氣流與顏色變化，讓你能聞到烘焙過程的香氣與節奏。"
            }
          },
          {
            "@type": "Question",
            "name": "可以在店裡買咖啡豆回家嗎？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "可以，店內提供自家烘焙咖啡豆與不定期豆單。若你不確定口味，可以告訴店員你喜歡的酸甜、香氣或濃度，我們會推薦合適的豆子。"
            }
          },
          {
            "@type": "Question",
            "name": "這裡的手沖咖啡走什麼風味路線？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "ONGU 以淺焙聞名，季節手沖常見花香、果韻與焦糖尾韻等層次。風味清晰但不誇張，適合喜歡乾淨、香氣明亮的精品咖啡派。"
            }
          },
          {
            "@type": "Question",
            "name": "第一次來 ONGU 信義店，點什麼不踩雷？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "想喝乾淨風味可從季節手沖或美式開始；喜歡奶香口感可選拿鐵。若你偏好更濃郁的咖啡感，也可以請店員依你口味調整推薦。"
            }
          },
          {
            "@type": "Question",
            "name": "這裡離信義威秀/台北101多遠？走路大概多久？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "距離信義威秀約 400 公尺，步行約 5 分鐘；距離台北 101 約 500 公尺左右。適合你在信義區逛街或辦公途中，安排一段聞香停留。"
            }
          },
          {
            "@type": "Question",
            "name": "週末或熱門時段人會很多嗎？建議什麼時間來？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "熱門時段可能需要稍等，尤其是季節手沖與外帶高峰。若你想更安靜地品飲，建議避開午后尖峰，提早來更容易選到喜歡的豆單。"
            }
          },
          {
            "@type": "Question",
            "name": "這間店適合讀書工作嗎？有插座/不限時嗎？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "這裡更像「職人現場＋聞香短停」的咖啡據點，店內不強調長時間工作型咖啡館氛圍。建議你把它當作在信義區放慢節奏、喝一杯好咖啡的角落。"
            }
          },
          {
            "@type": "Question",
            "name": "信義店有提供濾掛/掛耳或帶走的選擇嗎？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "視當期供應而定，通常會有適合帶走或回家沖煮的選項。你也可以直接詢問店員今天有哪些方便外帶或送禮的組合。"
            }
          },
          {
            "@type": "Question",
            "name": "ONGU 信義店跟一般商場咖啡廳有什麼不同？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "ONGU 更強調現場製作、烘豆與手沖的真實感，而不是吵雜的商場座位型咖啡館。你能看見製作過程、聞到烘焙香氣，體驗更像工作室的節奏。"
            }
          },
          {
            "@type": "Question",
            "name": "如果我想買「信義區咖啡豆推薦」等級的豆子，有什麼挑選建議？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "先告訴我們你喜歡偏花果香、偏甜感或偏厚實，我們會以當季豆單推薦合適的烘焙與產區。若你平常喝奶咖，也可以選擇更能支撐奶香的風味走向。"
            }
          },
          {
            "@type": "Question",
            "name": "商用咖啡豆批發：最低訂購量是多少？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "一般商業合作每次訂購以 2kg–5kg 為建議起跳，實際可依店內杯數與銷量討論彈性調整。"
            }
          },
          {
            "@type": "Question",
            "name": "商用咖啡豆批發：可以只買少量先試用嗎？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "可以，我們提供商業試用包與杯測體驗，適合準備開店或想更換豆單的店家先行測試。"
            }
          },
          {
            "@type": "Question",
            "name": "商用咖啡豆批發：是否提供磨粉與不同研磨度？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "如有需要，我們可依義式機、手沖、虹吸或美式機等設備調整研磨度，不過仍建議店家現場現磨，以保留香氣。"
            }
          },
          {
            "@type": "Question",
            "name": "商業豆與一般零售豆有什麼差別？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "商業豆會特別考量出杯穩定度與工作流程，配方設計更重視容錯率與風味一致性，方便前台每天大量萃取。"
            }
          },
          {
            "@type": "Question",
            "name": "想開新店，可以請你們協助規劃咖啡吧台與豆單嗎？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "可以，我們可依據預計客群、價格帶與設備配置，協助規劃豆單、配方與前台工作動線，讓你在開店前就把「咖啡這一塊」準備好。"
            }
          },
          {
            "@type": "Question",
            "name": "咖啡豆下單後多久會出貨？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "一般工作日下單後 1～3 個工作天內出貨，如遇大量訂單或特殊活動，可能略有調整，實際以出貨通知為主。若有急件需求，建議提前與我們聯繫。"
            }
          },
          {
            "@type": "Question",
            "name": "可以幫我把咖啡豆磨粉嗎？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "可以，下單時可於備註欄標註「手沖、摩卡壺、義式、虹吸壺」等沖煮器材，我們會依需求調整研磨度。不過為了保留最佳香氣，仍建議有研磨機的客人優先選擇整豆。"
            }
          },
          {
            "@type": "Question",
            "name": "ONGU Coffee 咖啡豆的保存期限多久？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "在未開封且保存良好的情況下，咖啡豆約可保存 6～9 個月；開封後建議於 2～3 個月內喝完為佳。ONGU Coffee 會在包裝上標註烘焙日期，方便你掌握最佳風味區間。"
            }
          },
          {
            "@type": "Question",
            "name": "目前咖啡豆網購配送範圍有哪些？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "目前提供台灣本島常溫配送，部分偏遠地區與離島可能需加收運費或延長配送時間，實際以結帳頁面顯示為準。若有海外大量採購需求，可透過聯絡我們與客服洽詢。"
            }
          },
          {
            "@type": "Question",
            "name": "第一次接觸精品咖啡豆，該怎麼選擇？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "若是第一次嘗試精品咖啡豆網購，建議從風味平衡、酸度較柔和的中焙咖啡豆開始，例如帶堅果、焦糖或可可風味的款式。你也可以參考 ONGU Coffee 網站上的「人氣推薦」標示，先從評價較高、適合大眾口味的豆單入門。"
            }
          }
        ]
      }
    ]
  };

  const storeFAQ = [
    {
      q: "Q1｜Ongu Studio 信義是什麼樣的店？",
      a: "A：我們專注於咖啡烘焙與沖煮，透過服務與產品讓消費者產生深入理解與探索咖啡的動力。"
    },
    {
      q: "Q2｜比較適合外帶還是內用？有位子嗎？",
      a: "A：可以內用，適合坐下來、放慢節奏，享受咖啡。如果你在信義區逛街或上班途中，外帶也很剛好。"
    },
    {
      q: "Q3｜這裡的咖啡風味是什麼路線？",
      a: "A：我們提供高乾淨度、水果調性、甜潤的咖啡豆，減少烘焙賦予的風味，保留生豆本質。"
    },
    {
      q: "Q4｜推薦的品項？特色是什麼？",
      a: "A：我們提供高品質咖啡豆，挑選市場少見的品種與獨立莊園。沖煮上保有個人風格，如果是第一次來，我們會推薦手沖咖啡，品味最純粹的風味。"
    },
    {
      q: "Q5｜離捷運 101 / 象山站大概多遠？",
      a: "A：從台北101站走過來大概 10 分鐘。"
    }
  ];

  const wholesaleFAQ = [
    {
      q: "Q1｜商業合作的最低訂購量是多少？",
      a: "A：實際數量可依品牌需求、銷量與出杯方式彈性討論。"
    },
    {
      q: "Q2｜可以先少量試用或杯測嗎？",
      a: "A：可以。我們提供樣品，適合準備開店、測試新豆單，或想更換咖啡豆的品牌。"
    },
    {
      q: "Q3｜能不能設計屬於自己品牌專屬的風味？",
      a: "A：可以。我們可以依品牌需求，製作出符合風格調性的咖啡豆。"
    },
    {
      q: "Q4｜新開店可以協助規劃豆單與吧台流程嗎？",
      a: "A：可以。我們協助規劃豆單方向、配方設定與前台動線，讓您在開業前就把「咖啡這一塊」準備好，而不是邊賣邊試。"
    }
  ];

  const onlineShoppingFAQ = [
    {
      q: "Q1｜咖啡豆下單後多久會出貨？",
      a: "A：一般工作日下單後約 3～5 個工作天內出貨。如遇大量訂單或特殊情況，實際時間以出貨通知為準。若有急件需求，歡迎先與我們聯繫確認。"
    },
    {
      q: "Q2｜可以幫我把咖啡豆磨粉嗎？",
      a: "A：可以，下單時備註沖煮器材（手沖、濃縮等），我們會依需求調整研磨度。不過為了保留最佳香氣，仍建議有條件的話選擇咖啡豆。"
    },
    {
      q: "Q3｜咖啡豆可以保存多久？什麼時候最好喝？",
      a: "A：未開封、保存良好的情況下，咖啡豆約可保存 3～6 個月；開封後建議 2～3 個月內喝完。烘焙後 20 天 是建議沖煮時間。"
    },
    {
      q: "Q4｜咖啡豆網購配送範圍有哪些？",
      a: "A：目前提供 台灣本島常溫配送。部分偏遠地區或離島可能需加收運費或延長配送時間，實際以結帳頁面顯示為準。若有海外或大量採購需求，歡迎與我們聯繫。"
    },
    {
      q: "Q5｜第一次接觸咖啡豆，該怎麼選？",
      a: "A：我們建議從風味敘述出發，先想想自己偏好的口感與香氣，再挑選最吸引你的豆子。"
    }
  ];

  const dripbagFAQ = [
    {
      q: "Q1. 濾掛咖啡禮盒適合送禮嗎？",
      a: "A：ONGU Coffee 濾掛咖啡禮盒採用自家烘焙精品咖啡豆製作，風味穩定、包裝完整，非常適合節慶送禮、企業贈禮與咖啡愛好者收藏。"
    },
    {
      q: "Q2. 濾掛咖啡與咖啡豆有什麼差別？",
      a: "A：濾掛咖啡適合快速沖泡與送禮使用，無需額外設備即可享用；而咖啡豆則適合自行研磨沖煮。ONGU Coffee 濾掛版本皆經實際測試，確保最佳萃取表現。"
    },
    {
      q: "Q3. 是否提供企業大量訂購？",
      a: "A：ONGU Coffee 提供企業大量訂購與客製化濾掛咖啡禮盒服務，可依需求調整數量、內容與搭配方式。"
    }
  ];

  const giftFAQ = [
    {
      q: "Q1. 為什麼選擇 ONGU Coffee 咖啡禮盒？",
      a: "A：ONGU Coffee 的咖啡禮盒不只是包裝咖啡，而是從咖啡豆挑選、烘焙、風味穩定度到禮盒設計皆由團隊控管。無論是商務送禮或私人贈禮，都能兼顧質感與實用性。"
    },
    {
      q: "Q2. 適合什麼場合送禮？",
      a: "A：我們的咖啡禮盒適合年節送禮、企業客戶贈禮、品牌合作贈品、開幕賀禮、生日禮物或謝師禮。咖啡禮盒內容可包含咖啡豆與搭配選品，彈性高、接受度高。"
    },
    {
      q: "Q3. 台北咖啡禮盒哪裡買？",
      a: "A：ONGU Coffee 提供線上選購與台北實體據點支援，不論自用或大量企業訂購，都能快速取得適合送禮的咖啡禮品。"
    }
  ];

  const gearFAQ = [
    {
      q: "Q1. 手沖壺怎麼選？",
      a: "A：穩定的水流是手沖咖啡的靈魂。ONGU 推薦的手沖壺強調出水穩定、握把舒適與設計感，適合日常沖煮與店家使用。"
    },
    {
      q: "Q2. 濾杯與濾紙如何搭配？",
      a: "A：不同濾杯結構與濾紙搭配，會帶來完全不同的萃取曲線。我們挑選適合 ONGU 烘焙曲線的濾杯，讓器具與咖啡豆互相加成。"
    },
    {
      q: "Q3. 在台北哪裡可以看實品？",
      a: "A：你可以在台北信義區 ONGU Studio 現場體驗部分咖啡器具，感受實際手感與尺寸，再決定是否入手。現場也有咖啡師可以分享器具搭配與使用建議。"
    }
  ];

  return (
    <div className="pt-24 pb-20 bg-white dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>FAQ｜Ongu Studio – Coffee Roasting（信義）｜門市與商用咖啡豆批發常見問題</title>
        <meta name="description" content="整理 ONGU Studio 信義店與商用咖啡豆批發常見問題：是否算信義區咖啡廳推薦、內用外帶、現場烘豆、咖啡豆選購、手沖風味、熱門時段建議，以及批發最低訂購量、試用包、研磨、吧台與豆單規劃。" />
        <meta name="keywords" content="信義區咖啡廳推薦,ONGU Studio 信義,信義咖啡店,職人手沖,現場烘豆,咖啡豆推薦,信義區咖啡豆推薦,商用咖啡豆批發,B2B咖啡豆,咖啡豆代工,濾掛OEM,杯測體驗" />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://www.ongucoffee.com/faq" />

        {/* OG Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Ongu Studio – Coffee Roasting" />
        <meta property="og:title" content="FAQ｜Ongu Studio – Coffee Roasting（信義）｜門市與商用咖啡豆批發常見問題" />
        <meta property="og:description" content="一次看懂 ONGU Studio 信義店與商用咖啡豆批發：現場烘豆、手沖風味、購豆建議，以及批發 MOQ、試用包、研磨與開店豆單規劃。" />
        <meta property="og:url" content="https://www.ongucoffee.com/faq" />
        <meta property="og:image" content="https://file.lumi.new/p373839333138763776/SPF2016947172312682496.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FAQ｜Ongu Studio – Coffee Roasting（信義）" />
        <meta name="twitter:description" content="門市＋商用咖啡豆批發常見問題整理：現場烘豆、手沖風味、購豆建議、MOQ、試用包、研磨與開店規劃。" />
        <meta name="twitter:image" content="https://file.lumi.new/p373839333138763776/SPF2016947172312682496.png" />

        {/* Schema */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        {/* Hidden H1 for SEO */}
        <h1 className="sr-only">FAQ｜信義區咖啡廳推薦與商用咖啡豆批發常見問題｜Ongu Studio – Coffee Roasting</h1>
        <p className="sr-only" data-ai-summary="true">
          本頁整理 Ongu Studio – Coffee Roasting（信義）門市與商用咖啡豆批發常見問題，包含：信義區咖啡廳推薦特色、內用外帶與座位、現場烘豆、咖啡豆選購與手沖風味建議、熱門時段與動線；並補充商業合作的最低訂購量、試用包、研磨需求、商業豆與零售豆差異，以及開店時豆單與吧台規劃支援。
        </p>

        {/* Section 1: Store FAQ */}
        <div className="mb-16">
          <button
            onClick={() => toggleSection('store')}
            className="w-full group hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-all duration-300 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 p-8"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-extralight tracking-[0.15em] text-gray-900 dark:text-gray-100 uppercase">
                QUESTION 1
              </h2>
              <ChevronDown
                size={24}
                className={`text-gray-400 dark:text-gray-500 transition-transform duration-300 ${
                  expandedSections['store'] ? 'rotate-180' : ''
                }`}
              />
            </div>
          </button>
          {expandedSections['store'] && (
            <div className="mt-6 space-y-10 px-8">
              <p className="text-sm font-light tracking-wide text-gray-600 dark:text-gray-400 mb-6">
                Store FAQ｜Ongu Studio -Coffee Roasting 信義
              </p>
              {storeFAQ.map((item, index) => (
                <div key={index} className="group border-l-2 border-gray-300 dark:border-gray-700 pl-8 py-6 hover:border-gray-500 dark:hover:border-gray-500 transition-all duration-300">
                  <h3 className="text-xl font-light tracking-wide text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <span className="text-gray-400 dark:text-gray-500 mr-3 font-extralight">Q{index + 1}</span>
                    {item.q.replace(/^Q[：:]/g, '')}
                  </h3>
                  <p className="text-base font-light leading-[2] text-gray-600 dark:text-gray-400">
                    {item.a.replace(/^A[：:]/g, '')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 2: Online Shopping FAQ */}
        <div className="mb-16">
          <button
            onClick={() => toggleSection('shopping')}
            className="w-full group hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-all duration-300 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 p-8"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-extralight tracking-[0.15em] text-gray-900 dark:text-gray-100 uppercase">
                QUESTION 2
              </h2>
              <ChevronDown
                size={24}
                className={`text-gray-400 dark:text-gray-500 transition-transform duration-300 ${
                  expandedSections['shopping'] ? 'rotate-180' : ''
                }`}
              />
            </div>
          </button>
          {expandedSections['shopping'] && (
            <div className="mt-6 space-y-10 px-8">
              <p className="text-sm font-light tracking-wide text-gray-600 dark:text-gray-400 mb-6">
                Online Shopping FAQ｜ONGU Coffee 咖啡豆網購
              </p>
              {onlineShoppingFAQ.map((item, index) => (
                <div key={index} className="group border-l-2 border-gray-300 dark:border-gray-700 pl-8 py-6 hover:border-gray-500 dark:hover:border-gray-500 transition-all duration-300">
                  <h3 className="text-xl font-light tracking-wide text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <span className="text-gray-400 dark:text-gray-500 mr-3 font-extralight">Q{index + 1}</span>
                    {item.q.replace(/^Q\d+[｜.]/g, '')}
                  </h3>
                  <p className="text-base font-light leading-[2] text-gray-600 dark:text-gray-400">
                    {item.a.replace(/^A[：:]/g, '')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 3: Wholesale FAQ */}
        <div className="mb-16">
          <button
            onClick={() => toggleSection('wholesale')}
            className="w-full group hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-all duration-300 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 p-8"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-extralight tracking-[0.15em] text-gray-900 dark:text-gray-100 uppercase">
                QUESTION 3
              </h2>
              <ChevronDown
                size={24}
                className={`text-gray-400 dark:text-gray-500 transition-transform duration-300 ${
                  expandedSections['wholesale'] ? 'rotate-180' : ''
                }`}
              />
            </div>
          </button>
          {expandedSections['wholesale'] && (
            <div className="mt-6 space-y-10 px-8">
              <p className="text-sm font-light tracking-wide text-gray-600 dark:text-gray-400 mb-6">
                Wholesale FAQ｜商用咖啡豆合作批發
              </p>
              {wholesaleFAQ.map((item, index) => (
                <div key={index} className="group border-l-2 border-gray-300 dark:border-gray-700 pl-8 py-6 hover:border-gray-500 dark:hover:border-gray-500 transition-all duration-300">
                  <h3 className="text-xl font-light tracking-wide text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <span className="text-gray-400 dark:text-gray-500 mr-3 font-extralight">Q{index + 1}</span>
                    {item.q.replace(/^Q\d+[｜.]/g, '')}
                  </h3>
                  <p className="text-base font-light leading-[2] text-gray-600 dark:text-gray-400">
                    {item.a.replace(/^A[：:]/g, '')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 4: Drip Bag FAQ */}
        <div className="mb-16">
          <button
            onClick={() => toggleSection('dripbag')}
            className="w-full group hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-all duration-300 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 p-8"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-extralight tracking-[0.15em] text-gray-900 dark:text-gray-100 uppercase">
                QUESTION 4
              </h2>
              <ChevronDown
                size={24}
                className={`text-gray-400 dark:text-gray-500 transition-transform duration-300 ${
                  expandedSections['dripbag'] ? 'rotate-180' : ''
                }`}
              />
            </div>
          </button>
          {expandedSections['dripbag'] && (
            <div className="mt-6 space-y-10 px-8">
              <p className="text-sm font-light tracking-wide text-gray-600 dark:text-gray-400 mb-6">
                Drip Bag FAQ｜濾掛咖啡禮盒
              </p>
              {dripbagFAQ.map((item, index) => (
                <div key={index} className="group border-l-2 border-gray-300 dark:border-gray-700 pl-8 py-6 hover:border-gray-500 dark:hover:border-gray-500 transition-all duration-300">
                  <h3 className="text-xl font-light tracking-wide text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <span className="text-gray-400 dark:text-gray-500 mr-3 font-extralight">Q{index + 1}</span>
                    {item.q.replace(/^Q\d+\.\s*/g, '')}
                  </h3>
                  <p className="text-base font-light leading-[2] text-gray-600 dark:text-gray-400">
                    {item.a.replace(/^A[：:]/g, '')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 5: Gift Box FAQ */}
        <div className="mb-16">
          <button
            onClick={() => toggleSection('gift')}
            className="w-full group hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-all duration-300 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 p-8"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-extralight tracking-[0.15em] text-gray-900 dark:text-gray-100 uppercase">
                QUESTION 5
              </h2>
              <ChevronDown
                size={24}
                className={`text-gray-400 dark:text-gray-500 transition-transform duration-300 ${
                  expandedSections['gift'] ? 'rotate-180' : ''
                }`}
              />
            </div>
          </button>
          {expandedSections['gift'] && (
            <div className="mt-6 space-y-10 px-8">
              <p className="text-sm font-light tracking-wide text-gray-600 dark:text-gray-400 mb-6">
                Gift Box FAQ｜咖啡禮盒
              </p>
              {giftFAQ.map((item, index) => (
                <div key={index} className="group border-l-2 border-gray-300 dark:border-gray-700 pl-8 py-6 hover:border-gray-500 dark:hover:border-gray-500 transition-all duration-300">
                  <h3 className="text-xl font-light tracking-wide text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <span className="text-gray-400 dark:text-gray-500 mr-3 font-extralight">Q{index + 1}</span>
                    {item.q.replace(/^Q\d+\.\s*/g, '')}
                  </h3>
                  <p className="text-base font-light leading-[2] text-gray-600 dark:text-gray-400">
                    {item.a.replace(/^A[：:]/g, '')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 6: Gear FAQ */}
        <div>
          <button
            onClick={() => toggleSection('gear')}
            className="w-full group hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-all duration-300 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 p-8"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-extralight tracking-[0.15em] text-gray-900 dark:text-gray-100 uppercase">
                QUESTION 6
              </h2>
              <ChevronDown
                size={24}
                className={`text-gray-400 dark:text-gray-500 transition-transform duration-300 ${
                  expandedSections['gear'] ? 'rotate-180' : ''
                }`}
              />
            </div>
          </button>
          {expandedSections['gear'] && (
            <div className="mt-6 space-y-10 px-8">
              <p className="text-sm font-light tracking-wide text-gray-600 dark:text-gray-400 mb-6">
                Gear FAQ｜咖啡器具
              </p>
              {gearFAQ.map((item, index) => (
                <div key={index} className="group border-l-2 border-gray-300 dark:border-gray-700 pl-8 py-6 hover:border-gray-500 dark:hover:border-gray-500 transition-all duration-300">
                  <h3 className="text-xl font-light tracking-wide text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <span className="text-gray-400 dark:text-gray-500 mr-3 font-extralight">Q{index + 1}</span>
                    {item.q.replace(/^Q\d+\.\s*/g, '')}
                  </h3>
                  <p className="text-base font-light leading-[2] text-gray-600 dark:text-gray-400">
                    {item.a.replace(/^A[：:]/g, '')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
